import React, { useEffect } from 'react'
import { View } from 'react-native'
import { useNavigation, NavigationProp } from '@react-navigation/native'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { PrimaryButton } from 'APP/Components/Buttons'
import ProgressBarCircle from 'APP/Components/ProgressBarCircle'
import DialogueTouchableOpacity from 'APP/Components/DialogueTouchableOpacity'
import ButtonMoreAction from 'APP/Components/ButtonMoreAction'
import { Colors, Metrics } from 'APP/Themes'
import I18n from 'APP/I18n'
import Retry from 'APP/Images/retry.svg'
import {
  Header,
  HeaderContainer,
  IllustrationImage,
  Loading,
  ProgressCopy,
  ProgressFraction,
  ProgressValue,
  ProgressWrapper,
  RetryContainer,
  RetryCtaStyle,
  RetryTitle,
  Subheading,
} from './style'
import Analytics from 'APP/Services/Analytics'
import styled from 'styled-components/native'
import Typography from 'APP/Converse/Typography'

const ButtonTextContainer = styled.View`
  padding: ${({ theme }) => theme.metrics.baseMargin * 0.9}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 0.25}px;
`

const MenuContainer = styled.View`
  position: absolute;
  top: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  right: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  align-items: flex-end;
`

interface WeeklyProgressData {
  weekly_progress?: {
    percentage: number
    minutes: number
  }
  weekly_goal?: number
}

interface MenuOption {
  value: string
  cancellable?: boolean
  enable?: boolean
  handler?: () => void
}

interface WeeklyProgressProps {
  retry: boolean
  loading: boolean
  refetch: () => void
  data?: WeeklyProgressData
  menuOptions?: MenuOption[]
}

const WeeklyProgress: React.FC<WeeklyProgressProps> = ({
  retry,
  loading,
  refetch,
  data,
  menuOptions,
}) => {
  const navigation = useNavigation<NavigationProp<Record<string, object | undefined>>>()
  const percentage = data?.weekly_progress?.percentage
  const achievedTarget = percentage ? percentage >= 100 : false

  const navigateToRecentHistory = () => {
    navigation.navigate('recentActiveHistory')
  }

  useEffect(() => {
    Analytics.trackScreen('Keep moving widget')
  }, [])

  return (
    <View>
      <HeaderContainer>
        <Header variant="h2" color="text" align="center">
          {achievedTarget
            ? I18n.t('ActiveMinutes.weeklyProgress.achieved.title')
            : I18n.t('ActiveMinutes.weeklyProgress.title')}
        </Header>
        {menuOptions?.length && (
          <MenuContainer>
            <ButtonMoreAction
              analyticsName="activeMinutesMenu"
              actions={
                [
                  ...(menuOptions || []),
                  {
                    value: I18n.t('ActiveMinutes.menu.cancel'),
                    cancellable: true,
                    enable: true,
                  },
                ] as unknown as never[]
              }
              size={24}
              onPress={() => {}}
              onPressActionSheetAction={() => {}}
            />
          </MenuContainer>
        )}
      </HeaderContainer>
      {!data && loading && <Loading size="large" color={Colors.text} testID="loading" />}
      {retry && (
        <RetryContainer testID="retry">
          <Retry fill={Colors.text} width={Metrics.icons.large} height={Metrics.icons.large} />
          <RetryTitle variant="h3" color="text">
            {I18n.t('ActiveMinutes.retry.title')}
          </RetryTitle>
          <Typography variant="bodyLarge" color="text">
            {I18n.t('ActiveMinutes.retry.copy')}
          </Typography>
          <PrimaryButton
            style={RetryCtaStyle}
            title={I18n.t('ActiveMinutes.retry.cta')}
            onPress={refetch}
            analyticsName="Retry activeMinutes"
          />
        </RetryContainer>
      )}
      {!retry && !!data && (
        <>
          <Subheading variant="bodyNormal" color="textSecondary" align="center" testID="subheading">
            {achievedTarget
              ? I18n.t('ActiveMinutes.weeklyProgress.achieved.subheading', {
                  activeMin: data?.weekly_goal,
                })
              : I18n.t('ActiveMinutes.weeklyProgress.subheading', {
                  activeMin: data?.weekly_goal,
                })}
          </Subheading>
          <View testID="progress">
            <IllustrationImage
              source={{ uri: UrlBuilder.activeMinutesAssets('illustration-left.png') }}
            />
            <ProgressBarCircle
              gradientWhenFull
              percentage={percentage}
              radius={100}
              strokeWidth={16}
              secondaryColor={achievedTarget ? Colors.habitDoneAccent : Colors.accent}
              text=""
              textFontSize={0}
              textStyles={{}}
            >
              <ProgressWrapper>
                <ProgressFraction>
                  <ProgressValue
                    variant="h1"
                    achieved={achievedTarget}
                    testID="progressFractionNumerator"
                  >
                    {data?.weekly_progress?.minutes || 0}
                  </ProgressValue>
                  <Typography variant="h1" color="text" testID="progressFractionDenominator">
                    {' '}
                    / {data?.weekly_goal || 0}
                  </Typography>
                </ProgressFraction>
                <ProgressCopy variant="bodyNormal" color="text">
                  {I18n.t('ActiveMinutes.weeklyProgress.minutesThisWeek')}
                </ProgressCopy>
              </ProgressWrapper>
              <DialogueTouchableOpacity
                onPress={navigateToRecentHistory}
                testID="navigateToRecentHistory"
                analyticsName="Explore"
              >
                <ButtonTextContainer>
                  <Typography variant="buttonText" align="center">
                    {I18n.t('ActiveMinutes.weeklyProgress.explore')}
                  </Typography>
                </ButtonTextContainer>
              </DialogueTouchableOpacity>
            </ProgressBarCircle>
            <IllustrationImage
              source={{ uri: UrlBuilder.activeMinutesAssets('illustration-right.png') }}
              alignRight
            />
          </View>
        </>
      )}
    </View>
  )
}

export default WeeklyProgress
